/**
 * Photo Tab Component
 *
 * Allows users to view and change their profile photo.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertCircle, Upload } from "lucide-react";
import type { User } from "@/services/authService";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiConfig } from "@/config/api";
import { apiFetch, UPLOAD_API_TIMEOUT_MS } from "@/lib/apiFetch";
import { PhotoCropDialog } from "@/components/common/PhotoCropDialog";
import { PersonalDataUploadConsentDialog } from "@/components/common/PersonalDataUploadConsentDialog";

interface PhotoTabProps {
  user: User;
}

export function PhotoTab({ user }: PhotoTabProps) {
  const { refreshUser } = useAuth();
  const [fotoFile, setFotoFile] = React.useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [showCropDialog, setShowCropDialog] = React.useState(false);
  const [cropImageData, setCropImageData] = React.useState<string | null>(null);
  const [consentOpen, setConsentOpen] = React.useState(false);

  // Handle file preview
  const handleFileChange = (file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const imageData = reader.result as string;
      setCropImageData(imageData);
      setShowCropDialog(true);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoCropComplete = (croppedImage: string) => {
    setFotoPreview(croppedImage);
    // Convert data URL to File for upload
    fetch(croppedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "foto.jpg", { type: "image/jpeg" });
        setFotoFile(file);
      });
  };

  const performPhotoUpload = async () => {
    if (!fotoFile) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("foto", fotoFile);

      await apiFetch(`${apiConfig.baseURL}/api/auth/upload-photos`, {
        method: "POST",
        credentials: "include",
        body: formData,
        timeoutMs: UPLOAD_API_TIMEOUT_MS,
      });

      toast.success("Foto profil berhasil diperbarui");
      setFotoFile(null);
      setFotoPreview(null);
      await refreshUser();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal mengunggah foto";
      setError(errorMessage);
      toast.error("Gagal mengunggah foto", { description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!fotoFile) {
      setError("Silakan pilih file foto");
      return;
    }

    setConsentOpen(true);
  };

  const handleCancel = () => {
    setFotoFile(null);
    setFotoPreview(null);
    setError("");
  };

  return (
    <div className="space-y-4">
      {/* Current Photo */}
      {user.foto && (
        <Card>
          <CardHeader>
            <CardTitle>Foto Profil Saat Ini</CardTitle>
            <CardDescription>Foto profil Anda yang tersimpan di sistem</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <img
                src={user.foto.startsWith("data:") || user.foto.startsWith("http") ? user.foto : `data:image/jpeg;base64,${user.foto}`}
                alt="Foto Profil"
                className="w-32 h-32 rounded-full object-cover border-4 border-primary"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload New Photo */}
      <Card>
        <CardHeader>
          <CardTitle>Ubah Foto Profil</CardTitle>
          <CardDescription>Upload foto profil baru Anda</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Photo Requirements Notice */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
            <p className="font-semibold text-amber-900 mb-2">⚠️ Ketentuan Foto Profil:</p>
            <ul className="space-y-1 text-amber-800 ml-4">
              <li>• Latar belakang merah polos (tanpa motif/garis/benda)</li>
              <li>• Foto harus jelas, terang, dan wajah terlihat utuh dari depan</li>
              <li>• Tidak menggunakan aksesoris penutup kepala kecuali untuk keperluan agama</li>
              <li>• Pencahayaan baik, tidak buram atau gelak</li>
            </ul>
            <p className="text-amber-800 mt-2 text-xs italic">
              Kami tidak bertanggung jawab atas ketidaksesuaian data jika foto yang diunggah berbeda dengan kondisi asli pemohon.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Foto Profil
              </Label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                  disabled={isLoading}
                  className="hidden"
                  id="foto-input"
                />
                <label
                  htmlFor="foto-input"
                  className="flex h-32 w-full items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground bg-muted/50 p-4 cursor-pointer hover:bg-muted transition-colors"
                >
                  {fotoPreview ? (
                    <img src={fotoPreview} alt="Preview" className="h-full object-cover rounded" />
                  ) : (
                    <div className="text-center">
                      <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                      <p className="text-xs text-muted-foreground">Klik untuk upload foto profil</p>
                    </div>
                  )}
                </label>
              </div>
              {fotoFile && <p className="text-xs text-green-600">✓ {fotoFile.name}</p>}
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading || !fotoFile}
              >
                Batal
              </Button>
              <Button type="submit" disabled={isLoading || !fotoFile}>
                {isLoading ? "Mengunggah..." : "Simpan Foto"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Photo Crop Dialog */}
      <PhotoCropDialog
        open={showCropDialog}
        onOpenChange={setShowCropDialog}
        imageData={cropImageData || ""}
        onCropComplete={handlePhotoCropComplete}
        aspectRatio={1}
        title="Crop Foto Profil"
      />

      <PersonalDataUploadConsentDialog
        open={consentOpen}
        onOpenChange={setConsentOpen}
        onConfirm={() => {
          setConsentOpen(false);
          void performPhotoUpload();
        }}
        confirmLabel="Unggah foto profil"
      />
    </div>
  );
}
